import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { parseTimeString } from './datepicker-value-parser';
import { addDays, isBefore } from 'date-fns';

export function NormalizeTime(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

export function AreDatesEqual(a: Date | string, b: Date | string): boolean {
  if (a == null && b == null) {
    return true;
  }
  if (a == null && b != null) {
    return false;
  }
  if (a != null && b == null) {
    return false;
  }
  if (typeof a == 'string') {
    a = new Date(a);
  }
  if (typeof b == 'string') {
    b = new Date(b);
  }
  return (
    a.getFullYear() == b.getFullYear() && a.getDate() == b.getDate() && a.getMonth() == b.getMonth()
  );
}

export function AreDateTimesEqual(a: Date | string, b: Date | string): boolean {
  if (a == null && b == null) {
    return true;
  }
  if (a == null && b != null) {
    return false;
  }
  if (a != null && b == null) {
    return false;
  }
  if (typeof a == 'string') {
    a = new Date(a);
  }
  if (typeof b == 'string') {
    b = new Date(b);
  }
  return a.getTime() == b.getTime();
}
export function IsAEarlierInDayThanB(a: Date, b: Date): boolean {
  if (a.getHours() < b.getHours()) {
    return true;
  } else if (a.getHours() > b.getHours()) {
    return false;
  } else {
    return a.getMinutes() < b.getMinutes();
  }
}

export function DoesRangeContainWeekends(
  start: Date | string | null,
  end: Date | string | null
): boolean {
  if (start == null || end == null) {
    return false;
  }
  if (typeof start == 'string') {
    start = new Date(start);
  }
  if (typeof end == 'string') {
    end = new Date(end);
  }

  const endNormalized = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  let day = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (day <= endNormalized) {
    const dayOfWeek = day.getDay();
    if (dayOfWeek == 0 || dayOfWeek == 6) {
      return true;
    }
    day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
  }
  return false;
}

export function GetTimeInEST(
  hours: number,
  minutes: number,
  date?: Date | undefined,
  seconds?: number | undefined,
  milliseconds?: number | undefined
) {
  const now = new Date();
  const estOffset = 5 * 60;
  const edtOffset = 4 * 60;
  let adjustedMinutes = minutes;
  const isDst = isDstActive(date ?? now);
  if (isDst) {
    adjustedMinutes += edtOffset;
  } else {
    adjustedMinutes += estOffset;
  }
  return new Date(
    Date.UTC(
      date?.getFullYear() ?? now.getFullYear(),
      date?.getMonth() ?? now.getMonth(),
      date?.getDate() ?? now.getDate(),
      hours,
      adjustedMinutes,
      seconds ?? 0,
      milliseconds ?? 0
    )
  );
}

function stdTimezoneOffset(date: Date) {
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
}

function isDstActive(date: Date) {
  return date.getTimezoneOffset() < stdTimezoneOffset(date);
}

export function minimumDate(dates: Date[]) {
  let minDate = null;
  for (let date of dates) {
    if (minDate == null || date < minDate) {
      minDate = date;
    }
  }

  return minDate;
}

export function maximumDate(dates: Date[]) {
  let maxDate = null;
  for (let date of dates) {
    if (maxDate == null || date > maxDate) {
      maxDate = date;
    }
  }

  return maxDate;
}

export function toNgbDate(date) {
  if (date == null) {
    return null;
  }

  if (date.getFullYear == null) {
    //We likely have a string, parse it
    date = new Date(date);
  }

  return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

export function fromNgbDate(date: NgbDate) {
  if (date == null) {
    return null;
  }
  return new Date(date.year, date.month - 1, date.day);
}

export function CountDaysBetweenTwoDates(startDate: Date | string, endDate: Date | string) {
  if (startDate == null || endDate == null) {
    return 0;
  }
  const start = typeof startDate == 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate == 'string' ? new Date(endDate) : endDate;
  if (!isBefore(start, end)) {
    return 0;
  }
  let current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  let count = 1;
  do {
    count++;
    current = addDays(current, 1);
  } while (!AreDatesEqual(current, end));
  return count;
}

export function GetDaysBetweenTwoDates(startDate: Date | string, endDate: Date | string) {
  if (startDate == null || endDate == null) {
    return 0;
  }
  const start = typeof startDate == 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate == 'string' ? new Date(endDate) : endDate;
  const endTimeStamp = end.getTime();
  const startTimeStamp = start.getTime();
  const differenceInTime = endTimeStamp - startTimeStamp;
  return Math.ceil(differenceInTime / (1000 * 3600 * 24));
}

export function GetHoursBetweenTwoTimes(startTime: Date | string, endTime: Date | string) {
  if (startTime == null || endTime == null) {
    return 0;
  }
  const start = typeof startTime == 'string' ? parseTimeString(startTime) : startTime;
  const end = typeof endTime == 'string' ? parseTimeString(endTime) : endTime;
  const endTimeStamp = end.getTime();
  const startTimeStamp = start.getTime();
  const differenceInTime = endTimeStamp - startTimeStamp;
  const differenceHours = +Math.abs(differenceInTime) / 3600000;
  return +differenceHours.toFixed(2);
}

export function GetHoursBetweenTwoDates(
  startDate: Date | string,
  endDate: Date | string,
  hours: number
) {
  if (startDate == null || endDate == null) {
    return 0;
  }
  if (typeof startDate == 'string') {
    startDate = new Date(startDate);
  }
  if (typeof endDate == 'string') {
    endDate = new Date(endDate);
  }

  //gets number of milliseconds between the two days.
  const differenceInTime = endDate.getTime() - startDate.getTime();
  //Converts into days. 86400000 = 1000 (number of milliseconds in a second) * 60 (seconds in a minute) * 60 (minutes in an hour) * 24 (hours in a day)
  //Adds 1 because the number of days leaves off the start date, and we need it to be inclusive.
  const numberOfDays = differenceInTime / 86400000 + 1;
  const hoursTaken = numberOfDays * hours;
  return +hoursTaken.toFixed(2);
}
